import { defaultLanguages, type ZudokuConfig } from "zudoku";
import { LandingPage } from "./src/components/LandingPage";
import { LegacyGpoPage } from "./src/components/LegacyGpoPage";
import { LanguageToggle } from "./src/components/LanguageToggle";

const config: ZudokuConfig = {
  port: 3000,
  docs: {
    files: "pages/**/*.{md,mdx}",
  },
  site: {
    title: "IZI Pay API Documentation",
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "IZI Pay",
      width: "118px",
    },
    showPoweredBy: false,
  },
  metadata: {
    title: "%s - IZI Pay",
    defaultTitle: "IZI Pay",
    favicon: "/favicon.svg",
  },
  syntaxHighlighting: {
    languages: [...defaultLanguages, "http", "php", "csharp"],
  },
  header: {
    placements: {
      search: "center",
    },
  },
  search: {
    type: "pagefind",
    maxResults: 8,
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  defaults: {
    apis: {
      disableSecurity: false,
    },
  },
  slots: {
    "head-navigation-start": (
      <div className="lg:hidden">
        <LanguageToggle />
      </div>
    ),
    "head-navigation-end": (
      <div className="flex items-center gap-3">
        <style>
          {`
            body:has(a[href="/api/legacy/payment-by-reference"][aria-current="page"]) .typography.prose-sm,
            body:has(a[href="/pt/api/legacy/payment-by-reference"][aria-current="page"]) .typography.prose-sm,
            body:has(a[href="/legacy/payment-by-reference/information"][aria-current="page"]) .typography.prose-sm,
            body:has(a[href="/pt/legacy/payment-by-reference/information"][aria-current="page"]) .typography.prose-sm {
              font-family: var(--font-sans) !important;
              font-size: 1rem !important;
            }

            html:not([lang="pt-AO"]) header nav li:has(> a[href^="/pt/"]) {
              display: none !important;
            }

            html:not([lang="pt-AO"]) [role="dialog"].lg\\:hidden li:has(> a[href^="/pt/"]) {
              display: none !important;
            }

            html[lang="pt-AO"] header nav li:has(> a[href="/introduction"]),
            html[lang="pt-AO"] header nav li:has(> a[href^="/portal/"]),
            html[lang="pt-AO"] header nav li:has(> a[href="/api/payment"]),
            html[lang="pt-AO"] header nav li:has(> a[href="/api/sms"]),
            html[lang="pt-AO"] header nav li:has(> a[href="/catalog"]),
            html[lang="pt-AO"] header nav li:has(> a[href="/legacy/payment-by-reference/information"]) {
              display: none !important;
            }

            html[lang="pt-AO"] [role="dialog"].lg\\:hidden li:has(> a[href="/introduction"]),
            html[lang="pt-AO"] [role="dialog"].lg\\:hidden li:has(> a[href^="/portal/"]),
            html[lang="pt-AO"] [role="dialog"].lg\\:hidden li:has(> a[href="/api/payment"]),
            html[lang="pt-AO"] [role="dialog"].lg\\:hidden li:has(> a[href="/api/sms"]),
            html[lang="pt-AO"] [role="dialog"].lg\\:hidden li:has(> a[href="/catalog"]),
            html[lang="pt-AO"] [role="dialog"].lg\\:hidden li:has(> a[href="/legacy/payment-by-reference/information"]) {
              display: none !important;
            }

            body:has(a[href="/api/legacy/payment-by-reference"][aria-current="page"]) header nav a[href="/legacy/payment-by-reference/information"],
            body:has(a[href^="/api/legacy/payment-by-reference/"][aria-current="page"]) header nav a[href="/legacy/payment-by-reference/information"],
            body:has(a[href="/api/legacy/gpo"][aria-current="page"]) header nav a[href="/legacy/payment-by-reference/information"],
            body:has(a[href="/pt/api/legacy/payment-by-reference"][aria-current="page"]) header nav a[href="/pt/legacy/payment-by-reference/information"],
            body:has(a[href^="/pt/api/legacy/payment-by-reference/"][aria-current="page"]) header nav a[href="/pt/legacy/payment-by-reference/information"],
            body:has(a[href="/pt/api/legacy/gpo"][aria-current="page"]) header nav a[href="/pt/legacy/payment-by-reference/information"] {
              color: var(--color-foreground) !important;
            }

            body:has(a[href="/api/legacy/payment-by-reference"][aria-current="page"]) header nav a[href="/legacy/payment-by-reference/information"]::after,
            body:has(a[href^="/api/legacy/payment-by-reference/"][aria-current="page"]) header nav a[href="/legacy/payment-by-reference/information"]::after,
            body:has(a[href="/api/legacy/gpo"][aria-current="page"]) header nav a[href="/legacy/payment-by-reference/information"]::after,
            body:has(a[href="/pt/api/legacy/payment-by-reference"][aria-current="page"]) header nav a[href="/pt/legacy/payment-by-reference/information"]::after,
            body:has(a[href^="/pt/api/legacy/payment-by-reference/"][aria-current="page"]) header nav a[href="/pt/legacy/payment-by-reference/information"]::after,
            body:has(a[href="/pt/api/legacy/gpo"][aria-current="page"]) header nav a[href="/pt/legacy/payment-by-reference/information"]::after {
              content: "";
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
              height: 0.125rem;
              background: var(--color-primary);
            }
          `}
        </style>
        <a
          href="https://backoffice.izipay.ao"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
        >
          IZI Pay Portal
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </a>
        <div className="h-4 w-px bg-border" />
        <LanguageToggle />
        <div className="h-4 w-px bg-border" />
      </div>
    ),
  },
  theme: {
    light: {
      background: "oklch(1.00 0 0)",
      foreground: "oklch(0.27 0 0)",
      card: "oklch(1.00 0 0)",
      cardForeground: "oklch(0.27 0 0)",
      popover: "oklch(1.00 0 0)",
      popoverForeground: "oklch(0.27 0 0)",
      primary: "oklch(0.77 0.16 70.08)",
      primaryForeground: "oklch(0 0 0)",
      secondary: "oklch(0.97 0.00 264.54)",
      secondaryForeground: "oklch(0.45 0.03 256.80)",
      muted: "oklch(0.98 0.00 247.84)",
      mutedForeground: "oklch(0.55 0.02 264.36)",
      accent: "oklch(0.99 0.02 95.28)",
      accentForeground: "oklch(0.47 0.12 46.20)",
      destructive: "oklch(0.64 0.21 25.33)",
      destructiveForeground: "oklch(1.00 0 0)",
      border: "oklch(0.93 0.01 264.53)",
      input: "oklch(0.93 0.01 264.53)",
      ring: "oklch(0.77 0.16 70.08)",
      radius: "0.375rem",
    },
    dark: {
      background: "oklch(0.20 0 0)",
      foreground: "oklch(0.92 0 0)",
      card: "oklch(0.27 0 0)",
      cardForeground: "oklch(0.92 0 0)",
      popover: "oklch(0.27 0 0)",
      popoverForeground: "oklch(0.92 0 0)",
      primary: "oklch(0.77 0.16 70.08)",
      primaryForeground: "oklch(0 0 0)",
      secondary: "oklch(0.27 0 0)",
      secondaryForeground: "oklch(0.92 0 0)",
      muted: "oklch(0.27 0 0)",
      mutedForeground: "oklch(0.72 0 0)",
      accent: "oklch(0.27 0 0)",
      accentForeground: "oklch(0.92 0.12 95.75)",
      destructive: "oklch(0.64 0.21 25.33)",
      destructiveForeground: "oklch(1.00 0 0)",
      border: "oklch(0.37 0 0)",
      input: "oklch(0.37 0 0)",
      ring: "oklch(0.77 0.16 70.08)",
      radius: "0.375rem",
    },
  },
  navigation: [
    {
      type: "custom-page",
      path: "/",
      label: "Home",
      display: "hide",
      element: <LandingPage />,
    },
    {
      type: "custom-page",
      path: "/pt",
      label: "Inicio",
      display: "hide",
      element: <LandingPage locale="pt" />,
    },
    {
      type: "category",
      label: "IZI Pay Docs",
      icon: "book-open",
      items: [
        {
          type: "category",
          label: "Getting Started",
          icon: "book-open",
          items: [
            "/introduction",
            "/environments",
            "/authentication",
            "/errors",
            "/rate-limits",
            {
              type: "link",
              icon: "languages",
              label: "Português",
              to: "/pt/introduction",
            },
          ],
        },
        {
          type: "category",
          label: "Portal Guide",
          icon: "panel-left",
          items: [
            "/portal/overview",
            "/portal/quick-payment",
            "/portal/qr-payment",
            "/portal/references",
            "/portal/payment-links",
            "/portal/payment-history",
            "/portal/refunds",
            "/portal/finance",
            "/portal/profile-settings-helpdesk",
            {
              type: "link",
              icon: "languages",
              label: "Português",
              to: "/pt/portal/overview",
            },
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Documentação IZI Pay",
      icon: "book-open",
      items: [
        {
          type: "category",
          label: "Introdução",
          icon: "book-open",
          items: [
            "/pt/introduction",
            "/pt/environments",
            "/pt/authentication",
            "/pt/errors",
            "/pt/rate-limits",
            {
              type: "link",
              icon: "languages",
              label: "English",
              to: "/introduction",
            },
          ],
        },
        {
          type: "category",
          label: "Manual do Portal",
          icon: "panel-left",
          items: [
            "/pt/portal/overview",
            "/pt/portal/quick-payment",
            "/pt/portal/qr-payment",
            "/pt/portal/references",
            "/pt/portal/payment-links",
            "/pt/portal/payment-history",
            "/pt/portal/refunds",
            "/pt/portal/finance",
            "/pt/portal/profile-settings-helpdesk",
            {
              type: "link",
              icon: "languages",
              label: "English",
              to: "/portal/overview",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      to: "/api/payment",
      label: "Payment API",
      icon: "code",
    },
    {
      type: "link",
      to: "/api/sms",
      label: "SMS Notification API",
      icon: "message-square",
    },
    {
      type: "link",
      to: "/catalog",
      label: "API Catalog",
      icon: "square-library",
    },
    {
      type: "link",
      to: "/pt/api/payment",
      label: "API de Pagamentos",
      icon: "code",
    },
    {
      type: "link",
      to: "/pt/api/sms",
      label: "API de Notificações SMS",
      icon: "message-square",
    },
    {
      type: "link",
      to: "/pt/catalog",
      label: "Catálogo de APIs",
      icon: "square-library",
    },
    {
      type: "category",
      label: "Legacy",
      icon: "history",
      items: [
        {
          type: "category",
          label: "Payment by Reference",
          icon: "receipt-text",
          items: [
            {
              type: "doc",
              label: "Information",
              icon: "book-open",
              file: "legacy/payment-by-reference/information",
            },
            {
              type: "link",
              label: "Referências",
              icon: "list",
              to: "/api/legacy/payment-by-reference/referencias",
            },
            {
              type: "link",
              label: "Pagamentos",
              icon: "credit-card",
              to: "/api/legacy/payment-by-reference/pagamentos",
            },
            {
              type: "link",
              label: "Notificações",
              icon: "bell",
              to: "/api/legacy/payment-by-reference/notificacoes",
            },
            {
              type: "doc",
              label: "Merchant Webhooks",
              icon: "webhook",
              file: "legacy/payment-by-reference/merchant-webhooks",
            },
          ],
        },
        {
          type: "category",
          label: "GPO",
          icon: "nfc",
          items: [
            {
              type: "custom-page",
              label: "Overview",
              icon: "book-open",
              path: "/api/legacy/gpo",
              element: <LegacyGpoPage />,
            },
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Legacy",
      icon: "history",
      items: [
        {
          type: "category",
          label: "Pagamento por Referência",
          icon: "receipt-text",
          items: [
            {
              type: "doc",
              label: "Informação",
              icon: "book-open",
              file: "pt/legacy/payment-by-reference/information",
            },
            {
              type: "link",
              label: "Referências",
              icon: "list",
              to: "/pt/api/legacy/payment-by-reference/referencias",
            },
            {
              type: "link",
              label: "Pagamentos",
              icon: "credit-card",
              to: "/pt/api/legacy/payment-by-reference/pagamentos",
            },
            {
              type: "link",
              label: "Notificações",
              icon: "bell",
              to: "/pt/api/legacy/payment-by-reference/notificacoes",
            },
            {
              type: "doc",
              label: "Webhooks de Comerciante",
              icon: "webhook",
              file: "pt/legacy/payment-by-reference/merchant-webhooks",
            },
          ],
        },
        {
          type: "category",
          label: "GPO",
          icon: "nfc",
          items: [
            {
              type: "custom-page",
              label: "Visão Geral",
              icon: "book-open",
              path: "/pt/api/legacy/gpo",
              element: <LegacyGpoPage locale="pt" />,
            },
          ],
        },
      ],
    },
  ],
  catalogs: [
    {
      path: "/catalog",
      label: "API Catalog",
      filterItems: (items) =>
        items.filter((item) => !item.path.startsWith("/pt/")),
    },
    {
      path: "/pt/catalog",
      label: "Catálogo de APIs",
      filterItems: (items) =>
        items.filter((item) => item.path.startsWith("/pt/")),
    },
  ],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml?api=authentication",
      path: "/api/authentication",
      categories: [{ label: "Identity", tags: ["Authentication", "MFA"] }],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.yaml?api=payment",
      path: "/api/payment",
      categories: [
        { label: "Payments", tags: ["Payments"] },
        { label: "PPR References", tags: ["PPR References"] },
        { label: "Terminal Operations", tags: ["Terminal Operations"] },
        { label: "Merchant Access", tags: ["Merchant Access"] },
      ],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.yaml?api=billing",
      path: "/api/billing",
      categories: [{ label: "Billing", tags: ["Billing"] }],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.yaml?api=sms",
      path: "/api/sms",
      categories: [
        { label: "Account", tags: ["SMS Account"] },
        { label: "Balance", tags: ["SMS Balance"] },
        { label: "Templates", tags: ["SMS Templates"] },
        { label: "Contacts", tags: ["SMS Contacts"] },
        { label: "Sending", tags: ["SMS Sending", "Scheduled SMS"] },
      ],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.pt.yaml?api=authentication",
      path: "/pt/api/authentication",
      categories: [{ label: "Identidade", tags: ["Autenticação", "MFA"] }],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.pt.yaml?api=payment",
      path: "/pt/api/payment",
      categories: [
        { label: "Pagamentos", tags: ["Pagamentos"] },
        { label: "Referências PPR", tags: ["Referências PPR"] },
        { label: "Operações de Terminal", tags: ["Operações de Terminal"] },
        { label: "Acesso de Comerciante", tags: ["Acesso de Comerciante"] },
      ],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./public/izipay-api-legacy.yml",
      path: "/api/legacy/payment-by-reference",
      categories: [
        {
          label: "Legacy — Payment by reference",
          tags: ["Referências", "Pagamentos", "Notificações"],
        },
      ],
      options: {
        showInfoPage: false,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./public/izipay-api-legacy.pt.yml",
      path: "/pt/api/legacy/payment-by-reference",
      categories: [
        {
          label: "Legacy — Pagamento por referência",
          tags: ["Referências", "Pagamentos", "Notificações"],
        },
      ],
      options: {
        showInfoPage: false,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.pt.yaml?api=billing",
      path: "/pt/api/billing",
      categories: [{ label: "Faturação", tags: ["Faturação"] }],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
    {
      type: "file",
      input: "./apis/openapi.pt.yaml?api=sms",
      path: "/pt/api/sms",
      categories: [
        { label: "Conta", tags: ["Conta SMS"] },
        { label: "Saldo", tags: ["Saldo SMS"] },
        { label: "Templates", tags: ["Templates SMS"] },
        { label: "Contactos", tags: ["Contactos SMS"] },
        { label: "Envio", tags: ["Envio SMS", "SMS Agendado"] },
      ],
      options: {
        showInfoPage: true,
        schemaDownload: { enabled: true },
      },
    },
  ],
};

export default config;
